import { useTranslation } from 'react-i18next'
import { useApiGetWeekIndicators } from 'core/api/home/weekly/useApiGetWeekIndicators'
import { useContext } from 'react'
import { HomeContext } from '../../../state/home-context'
import WidgetLoading from 'components/common/cards/widget-loading'
import DailyWeatherAndTarget from './daily-weather-and-target'
import DailyBoardMarginRates from './daily-board-margin-rates'
import DailyBoardProgressions from './daily-board-progressions'
import DailyBoardRealized from './daily-board-realized'
import { VpIconButton } from '@vtmn-play/react'
import { VpArrowRightIcon } from '@vtmn-play/icons/react'
import { useNavigate } from 'react-router'
import { getPathFromRouteId } from 'core/utils/route-utils'
import { RouteId } from 'core/routing/router/route-ids'
import { HOMEPAGE_CARD_REDIRECTS, HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../../../analytics/use-homepage-analytics'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'
import { useGetCurrencySymbol } from 'core/hooks/useGetCurrencySymbol'
import DateUtils from 'core/utils/date'

interface DailyBoardProps {
  yearWeek: string
  isCurrentWeek: boolean
}

const DailyBoard = ({ yearWeek, isCurrentWeek }: DailyBoardProps) => {
  const { t } = useTranslation()
  const { aggregation, teamId, channel, storeId, store } = useContext(HomeContext)
  const currency = store?.currency ?? ''
  const { data, isLoading } = useApiGetWeekIndicators(
    { storeId, teamId, channel, aggregation, week: yearWeek },
    isHomeApiEnabled(storeId, teamId, aggregation)
  )
  const getCurrencySymbol = useGetCurrencySymbol()

  const trackHomepageEvent = useHomepageAnalytics()
  const navigate = useNavigate()

  const navigateToDaily = (aggregation: string, teamId: number, storeId: number) => {
    trackHomepageEvent(HOMEPAGE_EVENTS.CARD_REDIRECT_CLICK, { card: HOMEPAGE_CARD_REDIRECTS.DAILY })
    const year = DateUtils.getYearNumberFromLabel(yearWeek)
    if (aggregation === 'STORE') {
      void navigate({
        pathname: getPathFromRouteId(RouteId.DailyStore),
        search: `?year=${year}&store_id=${storeId}`
      })
    } else if (aggregation === 'TEAM') {
      const teamSearch = teamId ? `&team=${teamId}` : ''
      void navigate({
        pathname: getPathFromRouteId(RouteId.DailyTeam),
        search: `?year=${year}&store_id=${storeId}${teamSearch}`
      })
    }
  }

  return (
    <>
      {isLoading ? (
        <WidgetLoading className="mg-daily mg-daily-loading" dataTestId="daily-board-loading" skeletonCount={8} />
      ) : null}
      {!isLoading && (
        <div className="mg-daily">
          <div className="mg-daily-day-column">
            <div className="mg-daily-title vp-caption">
              {t('general.homepage.daily.title')}
              <div className="mg-daily-cta">
                <VpIconButton
                  variant="secondary"
                  variantStyle="regular"
                  size="small"
                  onClick={() => {
                    navigateToDaily(aggregation, teamId, storeId)
                  }}
                >
                  <VpArrowRightIcon />
                </VpIconButton>
              </div>
            </div>
            <DailyWeatherAndTarget weeklyData={data?.weekly_data} isCurrentWeek={isCurrentWeek} />
          </div>
          <div className="mg-daily-column">
            <div data-testid="caption" className="mg-daily-board-header vp-caption">
              {t('general.homepage.daily.header.realized', {
                currency: currency ? getCurrencySymbol(currency) : '?'
              })}
            </div>
            <DailyBoardRealized weeklyData={data?.weekly_data} isCurrentWeek={isCurrentWeek} />
          </div>
          <div className="mg-daily-column">
            <div className="mg-daily-board-header vp-caption">{t('general.homepage.daily.header.progression')}</div>
            <DailyBoardProgressions weeklyData={data?.weekly_data} isCurrentWeek={isCurrentWeek} />
          </div>
          <div className="mg-daily-column">
            <div className="mg-daily-board-header vp-caption">{t('general.homepage.daily.header.margin')}</div>
            <DailyBoardMarginRates weeklyData={data?.weekly_data} isCurrentWeek={isCurrentWeek} />
          </div>
        </div>
      )}
    </>
  )
}

export default DailyBoard
